import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { MODAL_TYPE_DEMO_MODE } from '@wix/communities-blog-client-common';
import { connect } from '../components/runtime-context';
import createHocName from '../services/create-hoc-name';
import { getViewMode } from '../store/basic-params/basic-params-selectors';
import { getIsDemoMode } from '../store/instance-values/instance-values-selectors';
import { type Actions, type AppState } from '../types';
import withAuth, { type WithAuth } from './with-auth';

const NAME = 'PreventClickInDemoMode';

/** Props this HOC supplies; they disappear from the wrapped component's public API. */
export type PreventClickInDemoModeInjected = WithAuth & {
  isDemoMode: boolean;
  viewMode?: string;
  openModal: (type: string, payload?: Record<string, unknown>) => void;
};

export type WithPreventClickInDemoMode = PreventClickInDemoModeInjected & {
  /** Passed through by the caller and wrapped, so it stays public. */
  onClick?: (event: React.MouseEvent) => void;
};

/**
 * Props left for the caller once `Injected` is supplied by the HOC.
 *
 * Some wrapped components (e.g. Button) come back from a lodash `flowRight`
 * HOC with their type erased, so `P` cannot be inferred. Fall back to a
 * pass-through in that case instead of narrowing everything to `{}`.
 */
type PublicProps<P, Injected> = unknown extends P
  ? Record<string, unknown>
  : Omit<P, keyof Injected>;

export default function preventClickInDemoMode<P>(WrappedComponent: React.ComponentType<P>) {
  const PreventClickInDemoModeComponent = (props: P & WithPreventClickInDemoMode) => {
    const { isDemoMode, viewMode, onClick, openModal } = props;

    const handleClick = (event: React.MouseEvent) => {
      if (isDemoMode) {
        openModal(MODAL_TYPE_DEMO_MODE, { viewMode });
      } else {
        onClick && onClick(event);
      }
    };

    return <WrappedComponent {...props} onClick={handleClick} />;
  };
  PreventClickInDemoModeComponent.displayName = createHocName(NAME, WrappedComponent);

  hoistNonReactStatics(PreventClickInDemoModeComponent, WrappedComponent);

  const mapRuntimeToProps = (state: AppState, _ownProps: {}, actions: Actions) => ({
    viewMode: getViewMode(state),
    isDemoMode: getIsDemoMode(state),
    openModal: actions.openModal,
  });

  // react-redux cannot line its Matching<> constraint up through withAuth's Omit,
  // so the injected slice is stated here instead: everything in
  // WithPreventClickInDemoMode is supplied by this HOC, the rest stays on the caller.
  return connect(mapRuntimeToProps)(
    withAuth(PreventClickInDemoModeComponent) as React.ComponentType<{}>,
  ) as React.ComponentType<PublicProps<P, PreventClickInDemoModeInjected>>;
}
